import type { EventData } from '@/services/iframeMessenger';
import type { CheckoutData } from '@/types/checkout';
import type { ICheckoutContext, SubscriptionCheckoutState } from '@/types/context';
import { getSessionIdFromUrlParams } from './location';
import { getPaymentMethodDisplayName } from './paymentMethodResolvers';
import { toAppliedPromoCodeData } from './promoCode';

type FromContext = Partial<
  Pick<
    ICheckoutContext,
    | 'purchasedItems'
    | 'paymentMethodSelected'
    | 'paymentMethodsSettings'
    | 'promoCodeBreakdown'
    | 'customer'
    | 'pricing'
    | 'offer'
    | 'orderId'
  >
>;

type FromEventData = Partial<Pick<EventData, 'isSavedPaymentMethodUsed'>>;

type FromCheckoutData = Partial<CheckoutData>;

/** Subscription checkout only — the context slice, or its id on the subscription CheckoutData. */
export type FromSubscription = { subscription?: Pick<SubscriptionCheckoutState, 'subscriptionId'> };

export type EventDataSource = FromCheckoutData & FromContext & FromEventData & FromSubscription;

/**
 * Builds the shared iframe event payload from either checkoutData or checkout context.
 */
export const getEventDataForSdkEvents = (
  eventData: EventDataSource,
  additionalProperties?: Partial<EventData>
): EventData => {
  const selectedMethod = additionalProperties?.paymentMethodName ?? eventData?.paymentMethodSelected;
  const paymentMethods = eventData?.paymentMethodsSettings ?? eventData?.paymentMethods ?? [];
  const paymentMethodName = getPaymentMethodDisplayName(selectedMethod, paymentMethods);

  const promoFromState = eventData?.promoCodeBreakdown ? toAppliedPromoCodeData(eventData.promoCodeBreakdown) : null;
  const subscriptionId = eventData?.subscription?.subscriptionId;

  return {
    date: Date.now().toString(),
    sessionId: getSessionIdFromUrlParams(),
    bundleName: eventData?.offer?.name,
    bundleId: eventData?.offer?.sku,
    bundleSKU: eventData?.offer?.sku,
    products: (eventData?.items ?? eventData?.purchasedItems ?? [])?.map((item) => ({
      name: item.name,
      sku: item.sku,
      quantity: item.quantity,
    })) as unknown as EventData['products'],
    totalSum: eventData?.pricing?.priceBreakdown?.priceTotal,
    totalSumCurrency: eventData?.pricing?.currencyCode,
    userId: eventData?.customer?.id,
    userCountry: eventData?.customer?.geoLocator?.countryCode2,
    orderId: eventData?.orderId || '',
    isSavedPaymentMethodUsed: additionalProperties?.isSavedPaymentMethodUsed || false,
    isNewPaymentMethodSaved: additionalProperties?.isNewPaymentMethodSaved || false,
    ...(subscriptionId && { subscriptionId }),
    ...additionalProperties,
    paymentMethodName,
    promoCode: additionalProperties?.promoCode ?? promoFromState ?? null,
  };
};

/**
 * CLOSE_CHECKOUT must not carry a payment method — the shopper may close
 * before selecting one, and publishers should not treat close as a method-scoped event.
 */
export const getEventDataForCloseCheckout = (
  eventData: EventDataSource,
  additionalProperties?: Partial<EventData>
): EventData => ({
  ...getEventDataForSdkEvents(eventData, additionalProperties),
  paymentMethodName: undefined,
});
